import React, { FC, Fragment, useState, useEffect } from 'react';
import DebitCreditNotesActions from 'components/debit-credit-notes/debit-credit-notes-actions/debit-credit-notes-actions.component';
import DebitCreditNotesList from 'components/debit-credit-notes/debit-credit-notes-list/debit-credit-notes-list.component';
import CreateDebitCreditNoteDialog from 'components/debit-credit-notes/create-debit-credit-note-dialog/create-debit-credit-note-dialog.component';
import { Box } from '@mui/material';
import { DocumentNode, useMutation, ApolloCache, Reference } from '@apollo/client';
import { createInvoiceNoteTemplate, updateInvoiceNoteTemplate } from 'apollo/templates/invoices';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { InvoiceNoteFilter, InvoiceNote, Type } from 'models/invoice';
import { Organization } from 'models/organization';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

type DebitCreditNotesProps = {
    queryTemplate: DocumentNode;
    queryName: string;
};

const DebitCreditNotes: FC<DebitCreditNotesProps> = ({ queryTemplate, queryName }: DebitCreditNotesProps) => {
    const { t: translate } = useTranslation(['invoices']);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [invoiceNoteFilter, setInvoiceNoteFilter] = useState<InvoiceNoteFilter>({} as InvoiceNoteFilter);
    const initialInvoiceNoteValue = {
        _id: '',
        description: '',
        quantity: 0,
        itemPrice: 0,
        itemVat: 0,
        itemVatPercentage: 0,
        totalExclVat: 0,
        totalIncVat: 0,
        type: Type.DEBIT as Type,
        clinic: {} as Organization,
        dentist: undefined,
    };
    const [selectedInvoiceNote, setSelectedInvoiceNote] = useState<InvoiceNote>(initialInvoiceNoteValue);
    const [updatedNotes, setUpdatedNotes] = useState<InvoiceNote>({} as InvoiceNote);
    const { showBoundary } = useDenthubErrorBoundary();

    useEffect(() => {
        const filters = JSON.parse(
            localStorage.getItem('invoiceNoteListFilter') || '{"invoiceNoteStatus": "NOT_INVOICED"}',
        );
        setInvoiceNoteFilter(filters);
    }, []);

    const [createInvoiceNote, { loading: isInvoiceNoteCreationInProgress, error: invoiceNoteCreationErrors }] =
        useMutation(createInvoiceNoteTemplate, {
            onCompleted: ({ createInvoiceNote }) => {
                setUpdatedNotes(createInvoiceNote);
            },
            onError(error) {
                showBoundary(
                    error && (error?.graphQLErrors[0]?.extensions?.exception as any)?.type !== 'BAD_REQUEST'
                        ? error
                        : undefined,
                );
            },
            update(cache: ApolloCache<InvoiceNote>, { data: { createInvoiceNote } }) {
                cache.modify({
                    fields: {
                        invoiceNotes(invoiceNoteRefs: Reference | (Reference | undefined)[], { toReference }) {
                            if (invoiceNoteRefs instanceof Array) {
                                return [toReference(createInvoiceNote), ...invoiceNoteRefs];
                            }
                            return [toReference(createInvoiceNote), invoiceNoteRefs];
                        },
                    },
                });
            },
        });

    const [updateInvoiceNote, { loading: isInvoiceNoteUpdationInProgress, error: invoiceNoteUpdationErrors }] =
        useMutation(updateInvoiceNoteTemplate, {
            onCompleted: ({ updateInvoiceNote }) => {
                setUpdatedNotes(updateInvoiceNote);
            },
            onError(error) {
                showBoundary(
                    error && (error?.graphQLErrors[0]?.extensions?.exception as any)?.type !== 'BAD_REQUEST'
                        ? error
                        : undefined,
                );
            },
        });

    const handleAddDialogClose = () => {
        setIsAddDialogOpen(false);
        setSelectedInvoiceNote(initialInvoiceNoteValue);
    };

    const invoiceNoteFilterChangeHandler = (invoiceNoteFilter: InvoiceNoteFilter) => {
        setInvoiceNoteFilter(invoiceNoteFilter);
        localStorage.setItem('invoiceNoteListFilter', JSON.stringify(invoiceNoteFilter));
    };

    const openCreateInvoiceNoteDialog = () => {
        setIsAddDialogOpen(true);
    };

    const invoiceNoteClick = (invoiceNote: InvoiceNote) => {
        if (invoiceNote.invoice) {
            return;
        }
        setIsAddDialogOpen(true);
        setSelectedInvoiceNote(invoiceNote);
    };

    const onNoteAdd = (invoiceNote: InvoiceNote) => {
        if (invoiceNote._id === '') {
            delete invoiceNote._id;
            createInvoiceNote({
                variables: {
                    invoiceNote: Object.assign(invoiceNote, {
                        clinic: invoiceNote.clinic._id,
                        dentist: invoiceNote.dentist ? invoiceNote.dentist._id : undefined,
                    }),
                },
            });
        } else {
            const notesId = invoiceNote._id;
            const notesObj = Object.assign(invoiceNote, {
                clinic: invoiceNote.clinic._id,
                dentist: invoiceNote.dentist ? invoiceNote.dentist._id : undefined,
            });
            delete notesObj._id;
            updateInvoiceNote({
                variables: {
                    invoiceNote: notesObj,
                    invoiceNoteId: notesId,
                },
            });
        }
        setIsAddDialogOpen(false);
        setSelectedInvoiceNote(initialInvoiceNoteValue);
    };

    if (isInvoiceNoteCreationInProgress || isInvoiceNoteUpdationInProgress) {
        return <Loader />;
    }

    return (
        <Fragment>
            <Helmet>
                <title>{translate('invoices:DEBIT_CREDIT_NOTES')} | Denthub</title>
            </Helmet>
            {invoiceNoteCreationErrors &&
            (invoiceNoteCreationErrors?.graphQLErrors[0]?.extensions?.exception as any)?.type === 'BAD_REQUEST' ? (
                <AlertSnackbar type="error" message={invoiceNoteCreationErrors?.message} />
            ) : undefined}
            {invoiceNoteUpdationErrors &&
            (invoiceNoteUpdationErrors?.graphQLErrors[0]?.extensions?.exception as any)?.type === 'BAD_REQUEST' ? (
                <AlertSnackbar type="error" message={invoiceNoteUpdationErrors?.message} />
            ) : undefined}
            <Box mb={2}>
                <DebitCreditNotesActions
                    onInvoiceNoteFilterChange={invoiceNoteFilterChangeHandler}
                    filters={invoiceNoteFilter}
                    openCreateInvoiceNoteDialog={openCreateInvoiceNoteDialog}
                />
            </Box>
            <DebitCreditNotesList
                queryName={queryName}
                queryTemplate={queryTemplate}
                invoiceNoteFilter={invoiceNoteFilter}
                handleNoteClick={invoiceNoteClick}
                updatedNotes={updatedNotes}
            />
            <CreateDebitCreditNoteDialog
                open={isAddDialogOpen}
                onDialogClose={handleAddDialogClose}
                invoiceNote={selectedInvoiceNote}
                onNoteAdd={onNoteAdd}
            />
        </Fragment>
    );
};

export default DebitCreditNotes;
